import React, { useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import IconButton from '@material-ui/core/IconButton';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import Tooltip from '@material-ui/core/Tooltip';
import WalletConnect from './WalletConnect';
import { useAuth } from './auth/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { isMobile } from 'react-device-detect';

// Import images from config
import { futLogoIcon200, metamaskLogo } from './config';

const useStyles = makeStyles((theme) => ({
  header: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#333',
    color: '#fff',
    boxSizing: 'border-box',
    zIndex: 1000, // Ensure header is above other content
  },
  mobileHeaderRow: {
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 20px',
    '@media (max-width: 600px)': {
      padding: '8px 12px', // Reduced padding on mobile
    },
  },
  walletButtonRow: {
    display: 'flex',
    width: '100%',
    justifyContent: 'flex-end',
    marginTop: '4px',
  },
  walletInfo: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '14px',
  },
  // For controlling visibility on different screen sizes
  mobileOnly: {
    display: 'none',
    [theme.breakpoints.down('xs')]: {
      display: 'flex',
    },
  },
  desktopOnly: {
    display: 'flex',
    [theme.breakpoints.down('xs')]: {
      display: 'none',
    },
  },
  headerShortcutButton: {
    backgroundColor: 'transparent',
    color: '#ffffff',
    padding: '5px 10px',
    margin: '0 3px',
    fontSize: '0.78rem',
    borderRadius: '15px',
    border: 'none',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
    '&:focus': {
      outline: 'none',
    },
  },
  shortcutsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    alignItems: 'center',
    margin: '0 15px',
    overflow: 'hidden',
  },
  logo: {
    width: '32px',
    height: '32px',
    marginRight: '10px',
  },
  title: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    '@media (max-width: 600px)': {
      display: 'none', // Hide "E3D" text on mobile portrait
    },
  },
  loginButton: {
    backgroundColor: '#4d79ff',
    color: '#fff',
    border: 'none',
    borderRadius: '10px',
    padding: '5px 15px',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '14px',
    '&:hover': {
      backgroundColor: '#3a5bbf',
    },
  },
  username: {
    marginRight: '15px',
    fontSize: '14px',
    '@media (max-width: 600px)': {
      display: 'none', // Hide email/username in mobile portrait
    },
  },
  toolIconStyle: {
    color: '#fff',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
  },
}));

// Define shortcut buttons based on ShortcutPage.js, but with fewer buttons for header display
// Will be populated in the component with dialog handlers

function E3DHeader({
  isAuthenticated: propsIsAuthenticated,
  username: propsUsername,
  setShowOptionsPopup,
  setShowLogoutConfirm,
  setShowLogin,
  gotoMainPage,
  activeNodeId,
  activeNodeName,
  ethPrice,
  tokenPrice,
  tokenUniswapVersion,
  e3dPrice
}) {
  // Use the unified auth state from AuthContext to ensure we always have the most current values
  const { userAddress, isWalletConnected, connectWallet, disconnectWallet, isAuthenticated: contextIsAuthenticated } = useAuth();
  
  // Always prefer context authentication state, fall back to props
  const isAuthenticated = contextIsAuthenticated || propsIsAuthenticated || (localStorage.getItem('isAuthenticated') === 'true');
  
  // Get username from context, props, or localStorage
  const username = propsUsername || localStorage.getItem('username') || '';
  
  // Debug authentication state in header
  console.log('E3DHeader Authentication:', {
    contextIsAuthenticated,
    propsIsAuthenticated,
    localStorage: localStorage.getItem('isAuthenticated'),
    effectiveAuth: isAuthenticated,
    propsUsername,
    localStorageUsername: localStorage.getItem('username'),
    effectiveUsername: username
  });
  const classes = useStyles();
  const navigate = useNavigate();
  const location = useLocation();

  const goTo = (path) => {
    navigate(path, {
      state: { from: location.pathname }
    });
  };

  const goToGraphs = () => {
    try {
      window.location.assign('/graphs');
    } catch (e) {
      goTo('/graphs');
    }
  };
  
  // Handle special dialog buttons
  const handleOpenSwapDialog = () => {
    if (typeof window.openE3DSwapDialog === 'function') {
      window.openE3DSwapDialog();
    }
  };

  const handleOpenStakeDialog = () => {
    if (typeof window.openE3DStakeDialog === 'function') {
      window.openE3DStakeDialog();
    }
  };

  const handleOpenPreferences = () => {
    if (typeof setShowOptionsPopup === 'function') {
      setShowOptionsPopup(true);
    }
  };
  
  // Define buttons with special handlers for dialogs
  const headerButtons = [
    { label: 'Intelligence', path: '/intelligence', tooltip: 'Open the intelligence-first command center' },
    { label: 'Prices', path: '/price', tooltip: 'Check current token prices and stats' },
    { label: 'Stories', path: '/stories', tooltip: 'Read on-chain stories with E3D intelligence' },
    { label: 'Theses', path: '/thesis', tooltip: 'Fund manager dashboard: agent-generated trade theses' },
    { label: 'Actions', path: '/actions', tooltip: 'Decision-layer signals: per-thesis action signals derived from conviction, risk, and overlap' },
    { label: 'Verdicts', path: '/agent-verdicts', tooltip: 'Simulated capital decisions from the E3D Agent Verdict Engine' },
    { label: 'Graphs', path: '/graphs', tooltip: 'Explore wallet/token relationships and transaction evidence', hardNavigate: true },
    { label: 'Preferences', tooltip: 'Configure graph and app settings', handler: handleOpenPreferences },
    { label: 'Swap', tooltip: 'Swap ETH for E3D tokens directly', handler: handleOpenSwapDialog },
    { label: 'Stake', tooltip: 'Stake your E3D tokens for rewards', handler: handleOpenStakeDialog },
    { label: 'Pricing', path: '/signup', tooltip: 'View plans and upgrade to Premium' },
    { label: 'Bubbles', path: '/bubbles', tooltip: 'Display bubbles for tokens with highest price changes' },
    { label: 'Marketplace', path: '/marketplace', tooltip: 'Buy and sell E3D NFTs' },
    { label: 'Methodology', path: '/methodology', tooltip: 'Observable Transaction Admissibility — the measurement framework behind E3D' },
    { label: 'Docs', path: '/docs', tooltip: 'Open E3D documentation' },
    { label: 'API', path: '/api', tooltip: 'Manage API keys' },
    { label: 'News', path: '/news', tooltip: 'Read AI-generated crypto news' },
    { label: 'Pcap', path: '/pcap', tooltip: 'Load pcap and csv files' },
    { label: 'About', path: '/about', tooltip: 'Learn more about E3D' },
  ];

  const moreMenuLabelSet = useMemo(() => new Set(['Preferences', 'Swap', 'Stake', 'Pricing', 'Bubbles', 'Marketplace', 'Docs', 'API', 'News', 'Pcap', 'Methodology', 'About']), []);
  const [moreMenuAnchorEl, setMoreMenuAnchorEl] = React.useState(null);
  const moreMenuOpen = Boolean(moreMenuAnchorEl);

  const handleOpenMoreMenu = (e) => {
    setMoreMenuAnchorEl(e && e.currentTarget ? e.currentTarget : null);
  };

  const handleCloseMoreMenu = () => {
    setMoreMenuAnchorEl(null);
  };

  const desktopRenderButtons = useMemo(() => {
    const out = [];
    const baseButtons = headerButtons.filter((b) => !moreMenuLabelSet.has(b.label));

    baseButtons.forEach((b) => {
      out.push({ type: 'button', ...b });
    });
    out.push({ type: 'moreMenu' });

    return out;
  }, [headerButtons, moreMenuLabelSet]);

  const moreMenuButtons = useMemo(() => headerButtons.filter((b) => moreMenuLabelSet.has(b.label)), [headerButtons, moreMenuLabelSet]);

  return (
    <div id='e3dHeader' className={classes.header}>
      {/* Main header row with logo and login */}
      <div className={classes.mobileHeaderRow}>
        <div 
          style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} 
          onClick={gotoMainPage}
          title="E3D On-Chain Intelligence"
        >
          <IconButton 
            aria-controls="simple-menu" 
            aria-haspopup="true" 
            style={{paddingTop: 0, paddingBottom: 0, paddingLeft: 0, paddingRight: 10}}
          >
            <img src={futLogoIcon200} style={{width:'30px', height:'30px'}} alt="E3D Logo" />
          </IconButton>
          <span className={classes.title} style={{ cursor: 'pointer' }}>E3D</span>
        </div>

        {/* Shortcut buttons - only displayed on desktop */}
        {!isMobile && (
          <div className={classes.shortcutsContainer}>
            {desktopRenderButtons.map((item) => {
              if (item.type === 'moreMenu') {
                return (
                  <React.Fragment key="__more_menu__">
                    <Tooltip title="More" arrow placement="bottom">
                      <button
                        className={classes.headerShortcutButton}
                        onClick={handleOpenMoreMenu}
                        aria-haspopup="true"
                        aria-controls={moreMenuOpen ? 'e3d-header-more-menu' : undefined}
                      >
                        ...
                      </button>
                    </Tooltip>
                    <Menu
                      id="e3d-header-more-menu"
                      anchorEl={moreMenuAnchorEl}
                      keepMounted
                      open={moreMenuOpen}
                      onClose={handleCloseMoreMenu}
                      getContentAnchorEl={null}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                      PaperProps={{ style: { backgroundColor: '#333', color: '#fff', border: '1px solid rgba(255,255,255,0.15)' } }}
                    >
                      {moreMenuButtons.map(({ label, path, handler }) => (
                        <MenuItem
                          key={label}
                          onClick={() => {
                            try {
                              if (handler) handler();
                              else if (path) goTo(path);
                            } finally {
                              handleCloseMoreMenu();
                            }
                          }}
                          style={{ fontSize: '0.85rem' }}
                        >
                          {label}
                        </MenuItem>
                      ))}
                    </Menu>
                  </React.Fragment>
                );
              }

              const { label, path, tooltip, handler } = item;
              return (
                <Tooltip key={label} title={tooltip} arrow placement="bottom">
                  <button
                    className={classes.headerShortcutButton}
                    onClick={handler || (item.hardNavigate ? goToGraphs : (() => goTo(path)))}
                  >
                    {label}
                  </button>
                </Tooltip>
              );
            })}
          </div>
        )}
        
        {/* This content is shown on desktop */}
        <div className={classes.desktopOnly} style={{ alignItems: 'center', display: 'flex', marginRight: '40px', marginLeft: 'auto' }}>
          <button 
            className={classes.loginButton} 
            onClick={() => {isAuthenticated ? setShowLogoutConfirm(true) : setShowLogin(true)}}
          >
            {isAuthenticated ? "Logout" : "Login"}
          </button>
          
          {/* Wallet connect button on desktop */}
          <Tooltip title={userAddress ? "Disconnect Wallet" : "Connect Wallet"}>
            <IconButton 
              onClick={userAddress ? disconnectWallet : connectWallet} 
              className={classes.toolIconStyle} 
              style={{ 
                padding: 0, 
                marginLeft: '8px', 
                transition: 'box-shadow 0.3s', 
                boxShadow: userAddress ? '0 0 5px 2px limegreen' : 'none' 
              }}
            >
              <img src={metamaskLogo} style={{ width: '30px', height: '30px' }} alt="Connect Wallet" />
            </IconButton>
          </Tooltip>
          
          {/* Wallet address on desktop */}
          {userAddress && 
            <span className={classes.username} style={{ marginLeft: '8px', display: 'flex', alignItems: 'center' }}>
              {`${userAddress.slice(0, 6)}...${userAddress.slice(-4)}`}
            </span>
          }
        </div>
      </div>
    </div>
  );
}

export default E3DHeader;
